import { exec } from 'node:child_process'
import { chmod, existsSync } from 'node:fs'
import { mkdir } from 'node:fs/promises'
import { homedir } from 'node:os'
import { join } from 'node:path'
import { promisify } from 'node:util'

import type { DaemonInstallOptions } from './types.js'

const execAsync = promisify(exec)
const chmodAsync = promisify(chmod)

/**
 * Resolves a path with tilde expansion
 */
function resolvePath(path: string): string {
  if (path.startsWith('~/')) {
    return join(homedir(), path.slice(2))
  }
  return path
}

/**
 * Validates that a path is safe to use
 */
function validatePath(path: string): void {
  const resolved = resolvePath(path)
  if (resolved.includes('\0') || resolved.includes('\n') || resolved.includes('\r')) {
    throw new Error(`Invalid path: contains dangerous characters`)
  }
}

/**
 * Ensures ClickHouse binary is installed and returns the path to it.
 * Downloads using the official installer if not present.
 *
 * @param options - Installation options
 * @returns Path to the ClickHouse binary
 */
export async function ensureBinary(options: DaemonInstallOptions = {}): Promise<string> {
  const installDir = resolvePath(options.installDir || '~/.clickhouse')
  validatePath(installDir)
  const binDir = join(installDir, 'bin')
  const dataDir = join(installDir, 'data')
  const logDir = join(installDir, 'logs')
  const binaryPath = join(binDir, 'clickhouse')

  if (existsSync(binaryPath)) {
    return binaryPath
  }

  await mkdir(binDir, { recursive: true })
  await mkdir(dataDir, { recursive: true })
  await mkdir(logDir, { recursive: true })

  try {
    await execAsync(`cd "${binDir}" && curl https://clickhouse.com/ | sh`, {
      timeout: 120000,
    })

    if (!existsSync(binaryPath)) {
      throw new Error(`ClickHouse binary not found at ${binaryPath} after installation`)
    }

    await chmodAsync(binaryPath, 0o755)
    return binaryPath
  } catch (error) {
    if (error instanceof Error) {
      throw new Error(`Failed to install ClickHouse: ${error.message}`)
    }
    throw new Error('Failed to install ClickHouse: Unknown error')
  }
}

/**
 * Gets the installation directory paths for ClickHouse daemon
 */
export function getInstallPaths(options: DaemonInstallOptions = {}): {
  binaryPath: string
  binDir: string
  dataDir: string
  installDir: string
  logDir: string
} {
  const installDir = resolvePath(options.installDir || '~/.clickhouse')
  return {
    binaryPath: join(installDir, 'bin', 'clickhouse'),
    binDir: join(installDir, 'bin'),
    dataDir: join(installDir, 'data'),
    installDir,
    logDir: join(installDir, 'logs'),
  }
}
